import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Compass, Microscope, Wrench, Activity, ShieldCheck, Zap, TrendingUp, Rocket,
} from 'lucide-react';

const SERVICE_STAGES = [
  {
    step: '01',
    title: 'Consult',
    icon: Compass,
    items: [
      'Advisory & Consulting Services',
      'Digital Transformation Consulting',
      'Enterprise Architecture Advisory',
      'AI & Technology Strategy',
    ],
  },
  {
    step: '02',
    title: 'Assess',
    icon: Microscope,
    items: [
      'Audit & Assessment Services',
      'Operational Maturity Assessments',
      'Security & Compliance Assessments',
      'Observability & ITSM/ITOM Assessments',
    ],
  },
  {
    step: '03',
    title: 'Implement',
    icon: Wrench,
    items: [
      'Systems Integration Services',
      'Enterprise Application Services',
      'Cloud & Platform Engineering',
      'Infrastructure & Workplace Services',
    ],
  },
  {
    step: '04',
    title: 'Operate',
    icon: Activity,
    items: [
      'Managed Services & Support',
      'Enterprise Observability Services',
      'IT Service Management (ITSM)',
      'IT Operations Management (ITOM)',
    ],
  },
  {
    step: '05',
    title: 'Govern',
    icon: ShieldCheck,
    items: [
      'Cybersecurity, GRC & Compliance Services',
      'Risk Management',
      'Policy & Governance Management',
      'Audit & Compliance Management',
    ],
  },
  {
    step: '06',
    title: 'Automate',
    icon: Zap,
    items: [
      'Automation & Self-Healing Operations',
      'Workflow Automation',
      'Runbook Automation',
      'Autonomous Remediation',
    ],
  },
  {
    step: '07',
    title: 'Optimize',
    icon: TrendingUp,
    items: [
      'AIOps & Operational Intelligence',
      'Process Intelligence Services',
      'Performance Optimization',
      'Continuous Service Improvement',
    ],
  },
  {
    step: '08',
    title: 'Transform',
    icon: Rocket,
    items: [
      'AI & Agentic AI Services',
      'Digital Transformation Programs',
      'Autonomous Operations Enablement',
      'Enterprise Modernization Initiatives',
    ],
  },
];

const Services = () => {
  return (
    <section id="services" className="bg-[#0a0a0a] scroll-mt-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-[13px] uppercase tracking-[0.18em] text-neutral-500">Services</p>
            <h2 className="mt-3 text-[30px] sm:text-[40px] lg:text-[56px] leading-[1.04] sm:leading-[1.02] tracking-[-0.02em] font-semibold text-white max-w-3xl">
              Expertise that delivers. <span className="italic font-light text-neutral-400">Outcomes that compound.</span>
            </h2>
            <p className="mt-5 text-[16px] text-neutral-300 max-w-2xl leading-relaxed">
              Eight stages, one outcome — autonomous operations. From early advisory through enterprise-wide transformation, our teams and partner network are with you at every step.
            </p>
          </div>
          <a href="#" className="inline-flex items-center gap-2 text-[14px] font-medium text-white group" data-testid="services-explore-link">
            Explore all services
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SERVICE_STAGES.map((s) => {
            const Icon = s.icon;
            return (
              <Link
                key={s.step}
                to={`/services/${s.title.toLowerCase()}`}
                id={`services-${s.title.toLowerCase()}`}
                className="group bg-neutral-950 rounded-2xl p-6 border border-neutral-800 hover:border-blue-500 hover:-translate-y-0.5 transition-all scroll-mt-28 block"
                data-testid={`service-stage-${s.title.toLowerCase()}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-blue-400 font-semibold">Stage {s.step}</div>
                  <div className="w-10 h-10 rounded-xl bg-white text-neutral-950 flex items-center justify-center">
                    <Icon className="w-4.5 h-4.5" />
                  </div>
                </div>
                <h3 className="mt-5 text-[22px] font-semibold text-white tracking-tight">{s.title}</h3>
                <ul className="mt-4 space-y-2 pt-4 border-t border-neutral-900">
                  {s.items.map((it) => (
                    <li key={it} className="text-[12.5px] text-neutral-300 flex items-start gap-2 leading-relaxed">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-blue-500 flex-shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <span className="mt-5 inline-flex items-center gap-1.5 text-[12px] text-neutral-400 group-hover:text-white">
                  Explore stage <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            );
          })}
        </div>

        <div className="mt-14 rounded-3xl bg-neutral-950 border border-neutral-800 text-white p-10 lg:p-14 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <p className="text-[12px] uppercase tracking-[0.18em] text-neutral-500">Engagement Model</p>
            <h3 className="mt-3 text-[28px] lg:text-[36px] leading-[1.1] font-semibold">
              A proven path from <span className="italic font-light text-neutral-400">use case to enterprise scale.</span>
            </h3>
            <p className="mt-3 text-neutral-400 text-[15px] max-w-2xl">
              Discover, design, deploy, and scale — with named experts, transparent milestones, and measurable value at every stage.
            </p>
          </div>
          <div className="lg:col-span-4 grid grid-cols-2 gap-3">
            {['Discover', 'Design', 'Deploy', 'Scale'].map((p, i) => (
              <div key={p} className="rounded-xl bg-black border border-neutral-800 p-4">
                <div className="text-[11px] text-neutral-500">Phase 0{i + 1}</div>
                <div className="mt-1 text-[15px] font-medium">{p}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
